/**
 * Public specialist reads (M4-T03 endpoints). M4-T05 uses
 * `fetchHospitalSpecialists` (profile section); M4-T07 uses `fetchSpecialists`
 * (cross-hospital search screen). Read-only — booking is later-phase.
 */
import { apiFetch } from '../lib/api'
import type { SearchInterpretation } from './hospitals'

/** The closed 15 consultation specialties (CLAUDE.md). Slugs match the backend. */
export const SPECIALTIES: ReadonlyArray<{ slug: string; name: string }> = [
  { slug: 'cardiology', name: 'Cardiology' },
  { slug: 'orthopaedics', name: 'Orthopaedics' },
  { slug: 'obgyn', name: 'OBGYN' },
  { slug: 'oncology', name: 'Oncology' },
  { slug: 'neurosurgery', name: 'Neurosurgery' },
  { slug: 'paediatrics', name: 'Paediatrics' },
  { slug: 'dental', name: 'Dental' },
  { slug: 'ent', name: 'ENT' },
  { slug: 'dermatology', name: 'Dermatology' },
  { slug: 'internal-medicine', name: 'Internal Medicine' },
  { slug: 'general-surgery', name: 'General Surgery' },
  { slug: 'fertility-ivf', name: 'Fertility/IVF' },
  { slug: 'dialysis', name: 'Dialysis' },
  { slug: 'cosmetic', name: 'Cosmetic' },
  { slug: 'wellness', name: 'Wellness' },
]

/** Specialties the UI must be able to NAME but must not offer as a browse tile.
 *
 * General Practice is ADR-0062's destination 2 — where an ambiguous symptom
 * goes, and where a best fit nobody staffs falls back to. It is not a sixteenth
 * specialty to browse, and putting it in `SPECIALTIES` put it in the grid. But
 * the active-filter readout fell back to printing the raw slug, so a patient
 * sent to a general practitioner saw `general-practice`. */
export const NAMED_BEYOND_THE_FIFTEEN: ReadonlyArray<{
  slug: string
  name: string
}> = [{ slug: 'general-practice', name: 'General Practice' }]

/** A recurring weekly clinic session (M14-T04). `day_of_week` is 0 = Monday
 * (Python's convention; convert for JS `getDay()` via `sessionLabel`). */
export type ClinicSession = {
  id: string
  day_of_week: number
  part_of_day: string
  start_time?: string | null
  end_time?: string | null
}

/** A hospital a specialist practises at (cross-hospital search). */
export type SpecialistHospitalRef = {
  slug: string
  name: string
  city: string
  lat: number
  lng: number
  is_primary: boolean
  /** The hospital's operational line, verbatim from the server — which may
   * still be the seed placeholder. Never render it as a `tel:` without passing
   * it through `dialablePhone` (see `lib/phone.ts`). */
  phone?: string | null
  /** Hospital-admin free-text availability signal (M12-T05); null when unset. */
  availability_note?: string | null
  /** Recurring weekly clinic sessions at this hospital (M14-T04). */
  sessions?: ClinicSession[]
}

/** A specialist in the cross-hospital search results. */
export type Specialist = {
  id: string
  slug: string
  full_name: string
  specialty: string
  specialty_slug: string
  languages: string[]
  consult_fee_pesewas: number | null
  bio?: string | null
  photo_url?: string | null
  years_experience?: number | null
  qualifications?: string[] | null
  sub_specialties?: string[] | null
  hospitals: SpecialistHospitalRef[]
}

/** The search result: the specialists, and what the server understood the
 * query to mean. `interpretation` is null when the literal search answered and
 * nothing was inferred — so its presence is the signal that we guessed. */
export type SpecialistSearchResult = {
  data: Specialist[]
  interpretation: SearchInterpretation | null
}

/** React Query key for the cross-hospital specialist search. */
export function specialistsKey(specialty: string | null, q: string) {
  return ['specialists', specialty, q] as const
}

/** The specialist search, plus what the server made of the query.
 *
 * `interpretation` USED TO BE DISCARDED HERE. The server has sent it since
 * M22-T05 and this function ended `return body.data`, so the page could not have
 * rendered the disclosure even had it wanted to: an emergency phrase produced
 * "No specialists found. Try another specialty or name." The hospital results
 * screen read the same field and showed a red alert. Same server, same warning,
 * two different answers — found by the owner tapping through.
 *
 * Unlike `fetchHospitals`/`searchHospitals`, this is not split in two. That
 * split exists because several screens share the hospital list and only one
 * needs the interpretation; this function has exactly one caller. */
export async function fetchSpecialists(
  specialty: string | null,
  q: string,
  signal?: AbortSignal,
): Promise<SpecialistSearchResult> {
  const params = new URLSearchParams()
  if (specialty) params.append('specialty', specialty)
  if (q.trim()) params.append('q', q.trim())
  const qs = params.toString()
  const opts = signal ? { signal } : {}
  const body = await apiFetch<{
    data: Specialist[]
    interpretation?: SearchInterpretation | null
  }>(`/api/v1/specialists${qs ? `?${qs}` : ''}`, opts)
  return { data: body.data, interpretation: body.interpretation ?? null }
}

/** One of the closed 15 specialties with live counts (M12-T05). */
export type SpecialtyCount = {
  slug: string
  name: string
  specialist_count: number
  hospital_count: number
}

export function specialtyCountsKey() {
  return ['specialty-counts'] as const
}

/** GET /api/v1/specialties — the 15 specialties with per-specialty counts. */
export async function fetchSpecialtyCounts(
  signal?: AbortSignal,
): Promise<SpecialtyCount[]> {
  const opts = signal ? { signal } : {}
  const body = await apiFetch<{ data: SpecialtyCount[] }>(
    '/api/v1/specialties',
    opts,
  )
  return body.data
}

export function specialistKey(slug: string) {
  return ['specialist', slug] as const
}

/** GET /api/v1/specialists/{slug} — one specialist + the hospitals they
 * practise at (with `availability_note`). Throws `ApiError('not_found')`. */
export async function fetchSpecialist(
  slug: string,
  signal?: AbortSignal,
): Promise<Specialist> {
  const opts = signal ? { signal } : {}
  const body = await apiFetch<{ data: Specialist }>(
    `/api/v1/specialists/${slug}`,
    opts,
  )
  return body.data
}

/** A specialist as listed on one hospital's profile. */
export type HospitalSpecialist = {
  id: string
  slug: string
  full_name: string
  specialty: string
  specialty_slug: string
  languages: string[]
  consult_fee_pesewas: number | null
  is_primary: boolean
  bio?: string | null
  photo_url?: string | null
  years_experience?: number | null
  qualifications?: string[] | null
  sub_specialties?: string[] | null
  /** Hospital-admin free-text availability signal for this pairing (M12-T05); null when unset. */
  availability_note?: string | null
}

/** React Query key for a hospital's specialist roster. */
export function hospitalSpecialistsKey(slug: string) {
  return ['hospital-specialists', slug] as const
}

/** GET /api/v1/hospitals/{slug}/specialists — the hospital's specialists. */
export async function fetchHospitalSpecialists(
  slug: string,
  signal?: AbortSignal,
): Promise<HospitalSpecialist[]> {
  const opts = signal ? { signal } : {}
  const body = await apiFetch<{ data: HospitalSpecialist[] }>(
    `/api/v1/hospitals/${slug}/specialists`,
    opts,
  )
  return body.data
}

/** The "my specialist isn't listed" lead (M14-T09, ADR-0015). The closed 15
 * specialties stand; this captures the long tail (sub-specialties) as an AMTC
 * concierge lead rather than expanding the taxonomy. */
export type SpecialistRequestInput = {
  description: string
  details?: string
  area?: string
}
export type SpecialistRequestCreated = { reference: string; status: string }

/** POST /api/v1/patient/specialist-requests — capture a free-text specialist lead
 * for the AMTC concierge. Needs a verified patient session `token`. */
export async function submitSpecialistRequest(
  token: string,
  input: SpecialistRequestInput,
): Promise<SpecialistRequestCreated> {
  const body = await apiFetch<{ data: SpecialistRequestCreated }>(
    '/api/v1/patient/specialist-requests',
    {
      method: 'POST',
      token,
      body: {
        description: input.description,
        ...(input.details ? { details: input.details } : {}),
        ...(input.area ? { area: input.area } : {}),
      },
    },
  )
  return body.data
}
